import type {
    ColumnTextFilterSimple,
    ColumnTextFilterCombined,
    GridSettings,
    ColumnDefinition,
} from '~/components/Grid';
import { Form } from '~/lib/Api.types';
import { ReportType, TemplateIndex } from '../../ReportsIndexPage/Report.types';
import {
    CLIENT_KEY_PRIORITAZATION,
    EVENT_KEY_PRIORITAZATION,
    EventTypeColumnName,
    FilterDateFormatToUse,
    FORM_KEY_PRIORITAZATION,
    PACKAGE_GRANT_KEY_PRIORITAZATION,
    RevenuePerTypeGroupColumnName,
    RevenuePerTypeColumnsToSum,
    RevenuePerTypeColumnSuffixesToSum,
} from './FormsGrid.constants';
import DateTime from '~/lib/NewDateTime';
import { DateTimeBaseUnit } from '~/lib/NewDateTime/DateTime.types';

export const selectedTextArrayToGridFilter = (
    columnKeyToFilter: string,
    options: Array<string>,
    existingFilters: GridSettings['filterModel'] = {},
): GridSettings['filterModel'] => {
    if (!columnKeyToFilter || !options) {
        return null;
    }

    if (options.length === 0) {
        return {
            ...existingFilters,
            [columnKeyToFilter]: {
                filterType: 'text',
                type: 'blank',
            } as ColumnTextFilterSimple,
        };
    }

    const multipleOptionsExist = options.length > 1;
    if (multipleOptionsExist) {
        return {
            ...existingFilters,
            [columnKeyToFilter]: {
                filterType: 'text',
                conditions: options.map(aTextValue => ({
                    filterType: 'text',
                    type: 'contains',
                    filter: aTextValue,
                })),
                operator: 'OR',
            } as ColumnTextFilterCombined,
        };
    }

    return {
        ...existingFilters,
        [columnKeyToFilter]: {
            filterType: 'text',
            type: 'contains',
            filter: options[0],
        } as ColumnTextFilterSimple,
    };
};

export const gridFiltersToSelectedTextArray = (
    columnKeyToParse: string,
    existingFilters?: GridSettings,
): Array<string> => {
    if (!existingFilters?.filterModel?.[columnKeyToParse]) {
        return [];
    }
    const filtersForThatColumn = existingFilters.filterModel[columnKeyToParse];
    if (filtersForThatColumn?.conditions) {
        // if operator exists, it's a multi filter
        return filtersForThatColumn.conditions
            .map?.((condition: ColumnTextFilterSimple) => condition.filter)
            .filter(Boolean);
    } else {
        // otherwise if no operator, it's a simple filter
        return [
            (filtersForThatColumn as ColumnTextFilterSimple)?.filter,
        ].filter(Boolean) as Array<string>;
    }
};

export const selectedFormNamesToForms = (
    selectedFormNames?: Array<string>,
    allForms?: Array<Form>,
): Array<Form> => {
    if (
        !selectedFormNames ||
        selectedFormNames?.length === 0 ||
        !allForms ||
        allForms?.length === 0
    ) {
        return [];
    }
    return selectedFormNames
        .map(
            (
                formName: string, // for each selected form name string
            ) => allForms.find((aForm: Form) => aForm.name == formName), // go through allForms and return the form with that name
        )
        .filter(Boolean) as Array<Form>; // remove nullish values from the array
};

export const tweakColumnIfPremadeTemplateRelated = (
    columnKey: string,
    existingColumnConfig: ColumnDefinition,
    templateSelected?: TemplateIndex,
): ColumnDefinition => {
    if (templateSelected === TemplateIndex.REVENUE_PER_EVENT_TYPE) {
        if (columnKey === EventTypeColumnName) {
            return {
                ...existingColumnConfig,
                rowGroup: true, // Enable grouping
                hide: true, // Hide the column from direct view but use it for grouping
            };
        } else if (RevenuePerTypeColumnsToSum.includes(columnKey) || RevenuePerTypeColumnSuffixesToSum.some(suffix => columnKey.endsWith(suffix))) {
            return {
                ...existingColumnConfig,
                hide: false,
                aggFunc: 'sum', // Aggregate by summing
                headerName:
                    columnKey === RevenuePerTypeGroupColumnName
                        ? 'Revenue'
                        : undefined,
            };
        }
    }
    return existingColumnConfig;
};

export const sortReportColumns = (
    columns: Array<ColumnDefinition>,
    reportType?: ReportType,
) => {
    let keysPrioritazation: Array<string> = [];
    switch (reportType) {
        case ReportType.Events:
            keysPrioritazation = EVENT_KEY_PRIORITAZATION;
            break;
        case ReportType.Clients:
            keysPrioritazation = CLIENT_KEY_PRIORITAZATION;
            break;
        case ReportType.Forms:
            keysPrioritazation = FORM_KEY_PRIORITAZATION;
            break;
        case ReportType.PackageGrants:
            keysPrioritazation = PACKAGE_GRANT_KEY_PRIORITAZATION;
            break;
        case ReportType.Classes:
        default:
            return columns;
    }
    return columns.sort((a: ColumnDefinition, b: ColumnDefinition) => {
        const priorityA = keysPrioritazation.indexOf(a?.field ?? '');
        const priorityB = keysPrioritazation.indexOf(b?.field ?? '');

        // If both elements are in the priority array, sort by their index in the priority array
        if (priorityA !== -1 && priorityB !== -1) {
            return priorityA - priorityB;
        }

        // If one element is in the priority array, it should come first
        if (priorityA !== -1) return -1;
        if (priorityB !== -1) return 1;

        // Otherwise, keep the original order
        return 0;
    });
};

const getUTCDateOnly = (date: Date) => {
    return new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getDate()),
    );
};

export function datesComparator(
    date1?: string | Date,
    date2?: string | Date,
    unit: DateTimeBaseUnit = 'day',
    date1Format: string = FilterDateFormatToUse,
    date2Format: string = FilterDateFormatToUse,
) {
    if (!date1 || !date2) {
        console.error('datesComparator: Missing date strings');
        return -1;
    }
    const parseOptions = { zone: 'utc' };

    const stripTime = unit === 'day' || unit === 'month' || unit === 'year';

    // Parse the date strings into Date objects
    const date1Parsed =
        date1 instanceof Date
            ? DateTime.fromJsDate(stripTime ? getUTCDateOnly(date1) : date1, parseOptions)
            : DateTime.fromFormat(date1, date1Format, parseOptions);
    const date2Parsed =
        date2 instanceof Date
            ? DateTime.fromJsDate(stripTime ? getUTCDateOnly(date2) : date2, parseOptions)
            : DateTime.fromFormat(date2, date2Format, parseOptions);

    // Compare the two dates
    if (date1Parsed.isBefore(date2Parsed, unit)) {
        return 1;
    } else if (date1Parsed.isAfter(date2Parsed, unit)) {
        return -1;
    } else {
        return 0;
    }
}
